<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_BRANCH')"
            :label="$t('Add')"
            size="md"
            @click="addCorporateProfile"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          sm="12"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />

            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerProfileRef"
      :items="customerProfileList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="variantColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="getUserPermission('VIEW_CORPORATE_INTERNET_USERS')"
              @click="onSelectCustomerAccountItem(data.item, $event.target, 'view')"
            >
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_IB_PROFILE' : 'ACTIVATE_IB_PROFILE')"
              @click="productProfileStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="$t('Cancel')"
      ok-only
      :cancel-title="$t('Cancel')"
      ok-variant="outline-secondary"
      centered
      size="xl"
      :title="$t('Customer Information')"
    >
      <b-tabs>
        <b-tab
          :title="$t('Account Information')"
          active
        >
          <b-row class="match-height">
            <b-col md="6">
              <InputField
                id="customer-id"
                v-model="customer.customerID"
                :label="$t('Customer ID')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="account-name"
                v-model="customer.accountName"
                :label="$t('Account Name')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="account-type"
                v-model="customer.accountType"
                :label="$t('Account Type')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="branch"
                v-model="customer.branchCode"
                :label="$t('Branch')"
                :reduce="option => option.value"
                :options="branchOptions || []"
                item="label"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="product-type"
                v-model="customer.productCode"
                :label="$t('Product Type')"
                :reduce="option => option.value"
                :options="productTypeOptions || []"
                item="label"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="account-status"
                v-model="customer.accountStatus"
                :label="$t('Account Status')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="account-officer"
                v-model="customer.accountOfficerID"
                :label="$t('Account Officer')"
                :reduce="option => option.value"
                :options="allAccountOfficerOptions || []"
                item="label"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="account-tier-level"
                v-model.number="customer.accountTierLevel"
                :label="$t('Account Tier Level')"
                :options="accountTierOptions || []"
                item="label"
                :reduce="option => option.value"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="access-level"
                v-model.number="customer.accessLevel"
                :label="$t('Access Level')"
                :reduce="option => option.value"
                :options="accessLevelOptions || []"
                item="label"
                readonly
              />
            </b-col>
            <!--  Sector Code -->
            <b-col md="6">
              <SelectField
                id="means-of-id"
                v-model="customer.sectorCode"
                :label="$t('Sector Code')"
                :options="sectorCodeOptions || []"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                readonly
              />
            </b-col>
            <!--  Sector Code -->
            <!--  Category of Account -->
            <b-col md="6">
              <SelectField
                id="means-of-id"
                v-model="customer.categoryOfAccount"
                :label="$t('Category of Account')"
                :options="categoryOfAccountOptions || []"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                readonly
              />
            </b-col>
            <!--  Category of Account -->

            <b-col md="6">
              <SelectField
                id="delivery-mode"
                v-model="customer.statementDeliveryMode"
                :label="$t('Delivery Mode')"
                :reduce="option => option.value"
                :options="deliveryModeOptions || []"
                item="label"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="statement-delivery"
                v-model="customer.statementDeliveryFrequency"
                :label="$t('Frequency')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputNumberField
                id="minimum-balance"
                v-model.number="customer.minimumBalanceRequired"
                :label="$t('Minimum Balance Required')"
                readonly
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="currency-code"
                v-model="customer.currencyCode"
                :label="$t('Currency')"
                :reduce="option => option"
                :options="currencyCodeOptions || []"
                item="label"
                :placeholder="$t('Select currency')"
                readonly
              />
            </b-col>
          </b-row>

          <b-row class="d-flex align-items-center">
            <!--  Reference Number -->
            <b-col md="6">
              <InputField
                id="reference-number"
                v-model="customer.referenceNumber"
                :label="$t('Reference Number')"
                :placeholder="$t('Enter reference number')"
                type="text"
                show-icon
                :on-click="onViewInfo"
                readonly
              />
            </b-col>
            <!--  IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle"
                v-model="customer.employeeID"
                :label="$t('IPPIS/Oracle ID')"
                :placeholder="$t('Enter IPPIS/Oracle ID')"
                type="text"
                readonly
              />
            </b-col>
            <!--  IPPIS/Oracle ID -->
            <b-col md="6">
              <b-col
                md="10"
                class="mt-1"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <h6 class="">
                    {{ $t('Notify via') }}
                  </h6>

                  <b-form-checkbox
                    v-model="customer.enableEmailNotification"
                    inline
                    disabled
                  >
                    {{ $t('E-Mail') }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="customer.enableSMSNotification"
                    inline
                    disabled
                  >
                    {{ $t('SMS') }}
                  </b-form-checkbox>
                </div>
              </b-col>
            </b-col>
          </b-row>

          <b-row class="match-height">
            <b-col md="6">
              <SwitchButton
                v-model="customer.overrideProductApplicableInterest"
                :label="$t('Override Product Applicable Interest')"
                name="default-credit"
                class="mt-2"
                readonly
              />
              <div v-if="setDefaultApplicableInterest(customer.overrideProductApplicableInterest)">
                <b-row>
                  <b-col md="6">
                    <SelectField
                      id="credit-interest"
                      v-model="customer.applyCreditInterest"
                      :label="$t('Apply Credit Interest')"
                      :reduce="option => option.value"
                      :options="applyOptions || []"
                      :placeholder="$t('Select')"
                      item="label"
                      readonly
                    />
                  </b-col>

                  <!-- Conditional -->
                  <b-col
                    v-if="isConditional"
                    md="6"
                  >
                    <SelectField
                      id="conditional"
                      v-model="interestConditionBasis"
                      :label="$t('Conditional')"
                      :reduce="option => option.value"
                      :options="conditionalOptions || []"
                      :placeholder="$t('Select')"
                      item="label"
                      readonly
                    />
                  </b-col>

                  <b-col
                    v-if="isCreditInterest"
                    md="6"
                  >
                    <InputField
                      id="credit-interest-rate"
                      v-model.number="customer.interestRate"
                      :label="$t('Credit Interest Rate')"
                      type="number"
                      readonly
                    />
                  </b-col>
                </b-row>

                <div>
                  <b-form
                    v-if="isConditional"
                    ref="form"
                    class="repeater-form"
                  >
                    <!-- Row Loop -->
                    <b-row
                      v-for="item in customer.productInterests"
                      :id="item.interestConditionValue"
                      :key="item.interestConditionValue"
                      ref="productInterest"
                      class="mt-2 d-flex align-items-center"
                    >
                      <b-col
                        v-if="isInterestNoOfWithdrawal"
                        md="6"
                      >
                        <!-- No of Withdrawals(times) -->
                        <InputNumberField
                          id="number-of-withdrawal"
                          v-model.number="item.interestConditionValue"
                          :label="$t('No of Withdrawals')"
                          readonly
                        />
                      </b-col>

                      <b-col
                        v-if="!isInterestNoOfWithdrawal"
                        md="6"
                      >
                        <!-- Customer's Balance (Amount) -->
                        <InputNumberField
                          id="customer-balance"
                          v-model="item.interestConditionValue"
                          :label="$t('Customer\'s Balance (Amount)')"
                          readonly
                        />
                      </b-col>

                      <b-col md="6">
                        <!-- Credit Interest Rate -->
                        <InputNumberField
                          id="credit-interest-rate"
                          v-model.number="item.interestRate"
                          :label="$t('Rate')"
                          readonly
                        />
                      </b-col>
                    </b-row>
                  </b-form>
                </div>
              </div>
            </b-col>

            <b-col md="6">
              <SwitchButton
                v-model="customer.overrideProductApplicableFee"
                :label="$t('Override Product Applicable Fee')"
                name="cam-fee"
                class="mt-2"
                readonly
              />
              <div v-if="setProductApplicableFee(customer.overrideProductApplicableFee)">
                <b-row>
                  <b-col md="6">
                    <SelectField
                      id="apply-fee"
                      v-model="customer.applyFee"
                      :label="$t('Apply Fee')"
                      :options="applyOptions || []"
                      :placeholder="$t('Select')"
                      :reduce="option => option.value"
                      item="label"
                      readonly
                    />
                  </b-col>

                  <b-col
                    v-if="isConditionalFee"
                    md="6"
                  >
                    <SelectField
                      id="conditional"
                      v-model="feeConditionBasis"
                      :label="$t('Conditional')"
                      :options="conditionalOptions || []"
                      :placeholder="$t('Select')"
                      :reduce="option => option.value"
                      item="label"
                      readonly
                    />
                  </b-col>
                </b-row>

                <div
                  v-if="isConditionalFee"
                  class="mb-5"
                >
                  <b-form
                    ref="productFeeForm"
                    class="repeater-form"
                    @submit.prevent="repeatProductFees"
                  >
                    <!-- Row Loop -->
                    <b-row
                      v-for="item in customer.productFees"
                      :id="item.id"
                      :key="item.id"
                      ref="productFees"
                      class="align-items-center"
                    >
                      <b-col
                        v-if="isFeeNoOfWithdrawal"
                        md="6"
                      >
                        <!-- No of Withdrawals(times) -->
                        <InputNumberField
                          id="number-of-withdrawal"
                          v-model.number="item.feeConditionValue"
                          :label="$t('No of Withdrawal')"
                          readonly
                        />
                      </b-col>

                      <b-col
                        v-if="!isFeeNoOfWithdrawal"
                        md="6"
                      >
                        <!-- Customer's Balance (Amount) -->
                        <InputNumberField
                          id="customer-balance"
                          v-model.number="item.feeConditionValue"
                          :label="$t('Customer\'s Balance (Amount)')"
                          readonly
                        />
                      </b-col>
                      <!-- Conditional -->

                      <b-col md="6">
                        <!-- Flat Amount -->
                        <InputNumberField
                          id="flat-amt"
                          v-model.number="item.flatAmount"
                          :label="$t('Flat Amount')"
                          rules="required|positive"
                          :error-name="$t('Flat Amount')"
                          readonly
                        />
                      </b-col>
                      <b-col md="6">
                        <!-- Amount Per Transaction -->
                        <InputNumberField
                          id="amt-per-trans"
                          v-model.number="item.flatAmountPerTransaction"
                          :label="$t('Amount per Trans')"
                          readonly
                        />
                      </b-col>
                      <b-col md="6">
                        <!-- Percentage on Turnover -->
                        <InputField
                          id="percentage-on-turnover"
                          v-model.number="item.percentageOnTurnover"
                          :label="$t('% on Turnover')"
                          type="number"
                          readonly
                        />
                      </b-col>

                      <b-col md="6">
                        <!-- Minimum Amount To Charge -->
                        <InputNumberField
                          id="min-amount-to-charge"
                          v-model.number="item.minimumAmount"
                          :label="$t('Minimum Amount')"
                          readonly
                        />
                      </b-col>

                      <b-col md="6">
                        <!-- Maximum Amount To Charge -->
                        <InputNumberField
                          id="maximum-amount-to-charge"
                          v-model.number="item.maximumAmount"
                          :label="$t('Maximum Amount')"
                          readonly
                        />
                      </b-col>
                    </b-row>
                  </b-form>
                </div>
                <b-row v-if="isApplyFee">
                  <b-col md="6">
                    <InputNumberField
                      id="flat-amount"
                      v-model="customer.flatAmount"
                      :label="$t('Flat Amount')"
                      :placeholder="$t('Enter flat amount')"
                      type="number"
                      readonly
                    />
                  </b-col>

                  <b-col md="6">
                    <InputNumberField
                      id="flat-amount-per-transaction"
                      v-model="customer.flatAmountPerTransaction"
                      :label="$t('Flat Amount Per Transaction')"
                      :placeholder="$t('Enter flat amount per transaction')"
                      type="number"
                      readonly
                    />
                  </b-col>

                  <b-col md="6">
                    <InputNumberField
                      id="percentage-on-turnover"
                      v-model="customer.percentageOnTurnover"
                      :label="$t('Percentage on Turnover')"
                      :placeholder="$t('Enter percentage on turnover')"
                      type="number"
                      readonly
                    />
                  </b-col>

                  <b-col md="6">
                    <InputNumberField
                      id="maximum-amount-to-charge"
                      v-model.number="customer.maximumAmount"
                      :label="$t('Maximum Amount to Charge')"
                      :placeholder="$t('Enter maximum amount to charge')"
                      type="number"
                      readonly
                    />
                  </b-col>

                  <b-col md="6">
                    <InputNumberField
                      id="minimum-amount-to-charge"
                      v-model.number="customer.minimumAmount"
                      :label="$t('Minimum Amount to Charge')"
                      :placeholder="$t('Enter minimum amount to charge')"
                      type="number"
                      readonly
                    />
                  </b-col>
                </b-row>
              </div>
            </b-col>
          </b-row>
        </b-tab>

        <b-tab
          v-if="groupMemberList && groupMemberList.length"
          :title="$t('Group Members')"
        >
          <b-table
            ref="groupMemberListTable"
            select-mode="single"
            :items="groupMemberList"
            responsive
            :fields="translatedGroupMembersHeader"
            :per-page="tablePageSize"
            :current-page="tableCurrentPage"
            primary-key="id"
            selectable
            show-empty
            :empty-text="$t('No data available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          >
            <template #cell(SN)="data">
              {{ groupMemberTableIndex(data.index) }}
            </template>
          </b-table>
        </b-tab>

        <b-tab :title="$t('Signatories')">
          <b-table
            ref="signatoryListTable"
            select-mode="single"
            :items="signatoryList"
            responsive
            :fields="translatedSignatoryListHeaders"
            primary-key="id"
            selectable
            show-empty
            :empty-text="$t('No data available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          >
            <template #cell(SN)="data">
              {{ getOverallIndex(data.index) }}
            </template>

            <template #cell(passport)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="32"
                    :src="data.item.passport"
                    button
                    @click="onViewPassport(data.item.passport, data.item.fullName)"
                  />
                </template>
              </b-media>
            </template>

            <template #cell(signature)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="32"
                    :src="data.item.signature"
                    button
                    @click="onViewSignature(data.item.signature, data.item.fullName)"
                  />
                </template>
              </b-media>
            </template>

            <template
              #cell(actions)="data"
            >
              <Button
                variant="primary"
                size="sm"
                :label="data.item.isEnrolled ? $t('Resend Credentials') : $t('Enroll User')"
                :loading="data.item.isEnrolled ? isResendingCredentials : isEnrollingUser"
                :disabled="isResendingCredentials || isEnrollingUser"
                @click="data.item.isEnrolled ? handleResendCredentials(data.item) : handleEnrollUser(data.item)"
              />
            </template>
          </b-table>
        </b-tab>

        <b-tab :title="$t('Mandate Information')">
          <div>
            <b-table
              ref="mandateListTable"
              select-mode="single"
              :items="mandateList"
              responsive
              :fields="translatedMandateListHeaders"
              primary-key="id"
              selectable
              show-empty
              :empty-text="$t('No data available')"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              class="position-relative"
            >
              <template #cell(SN)="data">
                {{ getOverallIndex(data.index) }}
              </template>
              <template #cell(isLimited)="data">
                {{ data.value === false ? 'Unlimited' : 'Limited' }}
              </template>
              <template #cell(amount)="data">
                {{ data.value === 0 ? 'Unlimited' : toAmount(data.value) }}
              </template>
            </b-table>
          </div>
        </b-tab>
      </b-tabs>
    </b-modal>
    <!-- modal -->
    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCustomerProfile"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Customer ID  -->
        <b-col md="6">
          <InputField
            id="customer-id"
            v-model="filter.customerID"
            :label="$t('Customer ID')"
            :placeholder="$t('Enter customer ID')"
            type="text"
          />
        </b-col>
        <!-- Customer ID -->
        <b-col md="6">
          <InputField
            id="firstname"
            v-model="filter.firstName"
            :label="$t('First Name')"
            :placeholder="$t('Enter first name')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="last name"
            v-model="filter.lastName"
            :label="$t('Last Name')"
            :placeholder="$t('Enter last name')"
            type="text"
          />
        </b-col>

        <!-- Email  -->
        <b-col md="6">
          <InputField
            id="email"
            v-model="filter.email"
            :label="$t('Email')"
            :placeholder="$t('Enter email')"
            type="email"
          />
        </b-col>
        <!-- Email -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <b-modal
      id="avatar-modal"
      ref="avatarModal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="avatarModalTitle"
      @hidden="resetAvatarModal"
      @cancel="resetAvatarModal"
    >
      <div class="m-2 text-center">
        <b-row>
          <b-col cols="12">
            <b-avatar
              size="30rem"
              :src="avatarLg"
            />
          </b-col>
        </b-row>
      </div>

      <template #modal-footer="{}">
        <div />
      </template>
    </b-modal>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge, BForm,
  BDropdown, BModal, BSpinner, BDropdownItem, BTabs, BTab, BFormCheckbox, BMedia, BAvatar,
} from 'bootstrap-vue'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { toDateWithYear } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import convertZeroToString from '@/helpers/convertZeroToString'
import {
  toBalance, toKobo, toAmount, formatAmount,
} from '../../../../helpers/formatAmount'
import {
  frequency,
  gender, titleOptions, countries, relationshipOptions, pdfIcon, wordIcon, accountTier, applyInterest,
  users,
  accessLevel,
  deliveryMode,
  conditionalOptions, categoryOfAccountOptions,
  sectorCodeOptions,
} from '../../../../helpers/data'
import { eChannelsPermission } from '../../../../helpers/permissions'
import { transformMandateRule } from '../../../../helpers/transformers'
import getStatusColor from '@/helpers/getStatusColor'

const documentTypeOptions = [
  { value: 1, label: 'Signature Specimen' },
  { value: 2, label: 'Proof Of Address' },
  { value: 3, label: 'Driver’s License' },
  { value: 4, label: 'National Identity Card' },
  { value: 5, label: 'Voters Card' },
  { value: 6, label: 'National Identity Number' },
  { value: 7, label: 'International Passport' },
  { value: 8, label: 'Passport Photograph' },
  { value: 9, label: 'Reference Letter' },
  { value: 10, label: 'Guarantor’s Letter' },
  { value: 11, label: 'Loan Application Form' },
  { value: 12, label: 'Credit Bureau Form' },
  { value: 13, label: 'Others' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BSpinner,
    BDropdownItem,
    BModal,
    BBadge,
    BForm,
    BTabs,
    BTab,
    BFormCheckbox,
    BMedia,
    BAvatar,
    InputField,
    InputNumberField,
    SwitchButton,
    SelectField,
    TablePagination,
    Button,
    TableEntries,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isEditable: false,
      isResendingCredentials: false,
      isEnrollingUser: false,
      documentsFormData: [{
        type: '',
        url: '',
        file: '',
      }],
      fileExtension: '',
      pdfIcon,
      wordIcon,
      documentLoadSpinner: false,
      documentIdentifier: '',
      isDisabled: false,

      toDateWithYear,
      sortBy: 'customerID',
      buttonLabel: this.$t('Save'),
      isSelectLevelThree: false,
      isBusy: false,
      modalTitle: this.$t('Enrolled Customer Profile'),
      required,
      sortDesc: false,
      getStatusColor,
      customerProfileAccountListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountTierLevel', sortable: true, label: 'Account Tier Level' },
        { key: 'accountStatus', label: 'Status' },
      ],
      customerProfileAccountList: [],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Corporate Customer ID' },
        { key: 'corporateName', sortable: true, label: 'Corporate Customer Name' },
        { key: 'corporateAccountNumber', sortable: true, label: 'Corporate Account No' },
        { key: 'phoneNumber', sortable: true, label: 'Corporate Phone No' },
        { key: 'email', sortable: true, label: 'Corporate Email' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        ariaDateFormat: 'd/m/Y',
      },
      relationshipOptions,
      genderOptions: gender,
      titleOptions,
      customerProfileFormData: {
        customerID: '',
        title: '',
        firstName: '',
        lastName: '',
        otherNames: '',
        dateOfBirth: '',
        phoneNumber: '',
        email: '',
        userName: '',
        address: '',
        city: '',
        state: '',
        country: 'Nigeria',
        landmark: '',
        nokName: '',
        nokGender: '',
        nokPhoneNumber: '',
        nokEmail: '',
        nokAddress: '',
        nokRelationship: '',
        id: '',
      },
      filter: {
        customerID: '',
        firstName: '',
        lastName: '',
        email: '',
      },
      customerProfileList: [],
      countryOptions: ['Nigeria'],
      lgaOptions: [],

      toKobo,
      toAmount,
      toBalance,
      showSpinner: false,
      isCreditInterest: false,
      categoryOfAccountOptions,
      sectorCodeOptions,
      applyOptions: applyInterest,
      conditionalOptions,
      mandateListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'rule', sortable: true, label: 'Rule' },
        { key: 'isLimited', sortable: true, label: 'Limit' },
        { key: 'amount', sortable: true, label: 'Amount' },
      ],
      signatoryListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'passport', sortable: true, label: 'Passport' },
        { key: 'signature', sortable: true, label: 'Signature' },
        { key: 'actions', label: 'Actions' },
      ],
      groupMembersHeader: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'email', sortable: true, label: 'Email' },
      ],
      memoListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', label: 'Account Number' },
        { key: 'actionType', label: 'Action Type' },
        { key: 'details', label: 'Reason' },
        { key: 'comments', label: 'Comments' },
        { key: 'initiatedBy', label: 'Initiated By' },
      ],
      customerAccountList: [],
      customerAccountCache: {}, // { [cacheKey]: { data: [], totalCount: number } }
      cacheStale: false,
      productTypeOptions: [],
      accountOfficerOptions: [],
      allAccountOfficerOptions: [],
      signatoryList: [],
      signatoryMandateList: users,
      mandateList: [],
      refereeInformation: {},
      accountIntroducerList: {},
      memoGetData: [],
      customer: {
        customerID: '',
        accountName: '',
        accountType: '',
        branchCode: '',
        productCode: '',
        accountStatus: '',
        accountOfficerID: '',
        accountTierLevel: '',
        accessLevel: '',
        sectorCode: '',
        categoryOfAccount: '',
        signatories: {},
        overrideProductApplicableFee: false,
        applicableFee: 0,
        overrideProductApplicableInterest: false,
        applicableInterest: 0,
        statementDeliveryMode: '',
        statementDeliveryFrequency: '',
        enableEmailNotification: false,
        enableSMSNotification: false,
        minimumBalanceRequired: 0,
        accountCreationChannel: null,
        employeeID: '',
        mandateRuleConfig: {
          ruleConfig: [],
        },
        interestRate: null,
        applyCreditInterest: '',
        productFees: [],
        productInterests: [],
        applyFee: '',
        flatAmount: null,
        flatAmountPerTransaction: null,
        percentageOnTurnover: null,
        minimumAmount: null,
        maximumAmount: null,
        groupConnectionID: null,
        groupConnectionName: null,
      },
      timeout: null,
      feeConditionBasis: '',
      isApplyFee: false,
      isFeeNoOfWithdrawal: false,
      interestConditionBasis: '',
      isConditional: false,
      isConditionalFee: false,
      showAddProductInterest: false,
      productOptions: [],
      casaBalanceFormData: {
        accountNumber: '',
        accountName: '',
        productName: '',
        accountStatus: '',
        accountOfficerName: '',
        branchName: '',
        overdraftLimit: '',
        unclearedEffect: '',
        ledgerBalance: '',
        availableBalance: '',
        withdrawableBalance: 0,
        overdrawnAmount: 0,
        unpaidOverdraftInterest: 0,
        unpaidAccountOpeningFee: 0,
        unpaidAccountMaintenanceFee: 0,
        unpaidCOTFee: 0,
        unpaidOverdraftFee: 0,
        unpaidSMSFee: 0,
        totalOutstandingOverdraft: 0,
        unpaidLoanPrincipal: 0,
        unpaidLoanInterest: 0,
        unpaidLoanFee: 0,
        unpaidLoanPenalty: 0,
        totalOutstandingLoan: 0,
        accruedCOTFee: 0,
        accruedSMSFee: 0,
        accruedLoanInterest: 0,
        accruedOverdraftInterest: 0,
        totalAccruedAmount: 0,
        securityDeposit: 0,
        productMinimumBalance: 0,
        totalWithheldAmount: 0,
        percentageLien: 0,
        flatLien: 0,
        totalLien: 0,
        repaymentLien: 0,
        defaultingLoanInterest: 0,
      },
      accountLimitFormData: {
        accountNumber: '',
        user_Daily_Transaction_Limit: '',
        user_Single_Transaction_Limit: '',
        hasSpecialConsideration: false,
      },
      institutionData: {
        max_Single_Account_Transaction_Limit: 0,
        max_Daily_Account_Transaction_Limit: 0,
        specialLimit_Max_Single_Account_Transaction_Limit: 0,
        specialLimit_Max_Daily_Account_Transaction_Limit: 0,
      },
      avatarLg: '',
      avatarModalTitle: '',
      accountTierOptions: accountTier,
      accessLevelOptions: accessLevel,
      deliveryModeOptions: deliveryMode,
      frequencyOptions: frequency,
      groupMemberList: [],

      tablePageSize: 0,
      tableCurrentPage: 1,
      tablePerPage: 10,
      recordCount: 0,
      viewDocumentData: '',
      viewDocumentModalTitle: '',
      currencyCodeOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    stateOptions() {
      const country = countries.find(item => item.name === 'Nigeria')
      return country && country.states ? country.states.map(item => item.state).sort() : []
    },
    translatedCustomerProfileAccountListHeaders() {
      return this.customerProfileAccountListHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedDocumentTypeOptions() {
      return documentTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedMandateListHeaders() {
      return this.mandateListHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    translatedGroupMembersHeader() {
      return this.groupMembersHeader.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    translatedSignatoryListHeaders() {
      return this.signatoryListHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    translatedMemoListHeaders() {
      return this.memoListHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCustomerProfile()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCustomerProfile()
    },
  },
  mounted() {
    this.fetchCustomerProfile()
    this.fetchAllCurrencies()
  },
  methods: {
    ...mapActions({
      getCustomerProfile: 'eChannels/getCorporateCustomerProfileList',
      getAccountsByCustomerId: 'eChannels/getAccountsByCustomerId',
      updateCustomerProfile: 'eChannels/updateCustomerProfileAction',
      updateCustomerProfileStatus: 'eChannels/updateCustomerProfileStatus',
      getDocumentFiles: 'account/getDocumentFilesAction',
      getAllCurrencies: 'account/getAllCurrenciesAction',
      searchCustomerAccount: 'account/searchCustomerAccountAction',
      getAccountReferees: 'account/getAccountRefereesAction',
      getAccountSignatories: 'account/getAccountSignatoriesAction',
      getCustomerInformation: 'account/getCustomerInformationAction',
      getGroupMembers: 'account/getGroupMembersByGroupId',
      resendLoginToken: 'eChannels/resendLoginToken',
      getAccountOfficerByStaffID: 'account/getAccountOfficerByStaffID',
      createCorporateAccount: 'eChannels/createCorporateAccountAction',

    }),
    addCorporateProfile() {
      this.$router.push('add-corporate-profile')
    },
    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({ permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data
            .filter(item => item.status === 'Active')
            .reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    setDefaultApplicableInterest(value) {
      if (!value) {
        this.customer.applicableInterest = null
        return false
      }
      return true
    },
    setProductApplicableFee(value) {
      if (!value) {
        this.customer.applicableFee = null
        return false
      }
      return true
    },
    onViewInfo() {
      this.$root.$emit('bv::show::modal', 'info-modal')
    },
    onViewCustomerLimitInfo() {
      this.$root.$emit('bv::show::modal', 'info-customer-limit-modal')
    },
    viewDocumentFile(image, title, fileExtension) {
      this.fileExtension = fileExtension
      this.viewDocumentData = image
      this.viewDocumentModalTitle = title.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')
      this.$root.$emit('bv::show::modal', 'view-avatar-modal')
    },
    resetDocumentModal() {
      this.viewDocumentData = ''
      this.viewDocumentModalTitle = ''
    },
    handleDownload(data) {
      const link = document.createElement('a')
      link.href = data
      link.click()
      this.alertSuccess({ text: this.$t('Your request to download file was successful') })
    },
    async handleResendCredentials(item) {
      try {
        this.isResendingCredentials = true
        const response = await this.resendLoginToken(
          {
            formData: {
              userId: item.id,
            },
            permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
          },
        )
        if (response.status === 200) {
          const text1 = this.$t('Your request to resend login credentials for')
          const text2 = this.$t('was successful')
          this.alertSuccess({ text: `${text1} ${item.fullName} ${text2}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isResendingCredentials = false
      }
    },
    async handleEnrollUser(item) {
      try {
        this.isEnrollingUser = true

        const formData = {
          accountNumber: item.corporateAccountNumber,
          signatories: [{
            customerID: item.customerID,
            role: '',
          }],
        }

        const response = await this.createCorporateAccount({ formData, permission: eChannelsPermission.VIEW_INTERNET_BANKING_USERS })

        const { status, data } = response
        if (status === 200) {
          this.alertSuccess({ text: data.message })
          this.isEnrollingUser = false
          this.$router.push({
            name: 'enroll-corporate-profile',
          })

          const text1 = this.$t('Successfully enrolled')
          this.alertSuccess({ text: `${text1} ${item.fullName}` })
        }
      } catch (error) {
        if (error && error.response && error.response.data) {
          this.handleError(error)
        } else {
          this.handleError({ message: error?.message || 'An unexpected error occurred.' })
        }
      } finally {
        this.isEnrollingUser = false
      }
    },
    getLGAByState(selectedValue) {
      const country = countries.find(item => item.name === 'Nigeria')
      if (country && country.states) {
        const selectedState = country.states.find(item => item.state === selectedValue)
        this.lgaOptions = selectedState && selectedState.lgas ? selectedState.lgas.sort() : []
      } else {
        this.lgaOptions = []
      }
    },
    async fetchCustomerProfile() {
      this.isBusy = true
      this.hideModal('modal-filter')
      try {
        const response = await this.getCustomerProfile({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.customerProfileList = data.data.map(items => ({
            ...items,
            email: items.corporateEmail ? items.corporateEmail.toLowerCase() : '',
            corporateAccountNumber: items.corporateAccountNumber ?? '',
            phoneNumber: items.corporatePhoneNumber ?? '',
            status: items.isActive === true ? 'Active' : 'InActive',
          }))
          this.totalCount = data.recordCount
        } else {
          this.customerProfileList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async getDocumentByDocumentIdentifier(documentIdentifier) {
      try {
        const response = await this.getDocumentFiles({
          referenceNumber: documentIdentifier,
          permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.documentsFormData = data.data ? data.data.map(item => ({
            url: item.url,
            type: item.typeID,
            fileName: item.type,
            id: item.id,
            fileExtension: item.fileExtension,
          })) : [{
            url: '',
            type: '',
          }]
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.enrollCustomerProfileRules.validate().then(async success => {
          if (success) {
            const areYouSure = this.$t('Are you sure you want to')
            const thisText = this.$t('this Enrolled Customer Profile?')
            this.confirm(this.customerProfileAction, { text: `${areYouSure} ${this.buttonLabel} ${thisText}` })
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.hideModal('modal-lg')
      }
    },
    async customerProfileAction() {
      try {
        this.isLoading = true
        const formData = {
          firstName: this.customerProfileFormData.firstName,
          lastName: this.customerProfileFormData.lastName,
          otherNames: this.customerProfileFormData.otherNames,
          phoneNumber: this.customerProfileFormData.phoneNumber,
          email: this.customerProfileFormData.email,
          userName: this.customerProfileFormData.userName,
          address: this.customerProfileFormData.address,
          id: this.customerProfileFormData.id,
        }
        const response = await this.updateCustomerProfile({ formData, permission: eChannelsPermission.UPDATE_IB_PROFILE })
        const { status } = response
        if (status === 200) {
          this.fetchCustomerProfile()
          this.hideModal('modal-lg')
          const text1 = this.$t('Your request to')
          const text2 = this.$t('Enrolled Customer Profile was successful')
          this.alertSuccess({ text: `${text1} ${this.buttonLabel} ${text2}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    productProfileStatus(items) {
      const {
        id, lastName, firstName, email, phoneNumber, customerID, isActive,
      } = items
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? eChannelsPermission.DEACTIVATE_IB_PROFILE : eChannelsPermission.ACTIVATE_IB_PROFILE

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            lastName,
            firstName,
            email,
            phoneNumber,
            customerID,
            isActive,
          }
          const response = await this.updateCustomerProfileStatus({
            statusValues, routeName: action, permission,
          })
          if (response.status === 200) {
            this.fetchCustomerProfile()
            const text1 = this.$t('Your request to')
            const successText = this.$t('this Enrolled Customer Profile was successful')
            this.alertSuccess({ text: `${text1} ${action} ${successText}` })
            this.showSpinner = false
          }
        } catch (error) {
          const text1 = this.$t('Your request to')
          const text2 = this.$t('this Enrolled Customer Profile failed. Reason:')
          this.alertError({ text: `${text1} ${action} ${text2} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const thisText = this.$t('this Enrolled Customer Profile?')
      this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${thisText}` })
    },

    async onSelectProductProfileItem(items, activity, val) {
      try {
        const response = await this.getAccountsByCustomerId({ customerId: items.customerID })
        const { data } = response
        this.customerProfileAccountList = data.data
        this.customerProfileFormData = {
          ...items,
          dateOfBirth: items.dateOfBirth ? toDateWithYear(items.dateOfBirth) : '',
        }
        this.getDocumentByDocumentIdentifier(items.documentIdentifier)
        this.isEditable = true
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Enrolled Customer Profile') : this.$t('View Enrolled Customer Profile')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchAccountReferees(accountNumber) {
      try {
        const response = await this.getAccountReferees({
          accountNumber,
          permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
        })

        if (response && response.status === 200 && response.data && response.data.data && Array.isArray(response.data.data)) {
          this.refereeInformation = response.data.data.reduce((acc, item, index) => {
            const dynamicKey = `referee${index + 1}`
            return {
              ...acc,
              [dynamicKey]: `${item.lastName || ''} ${item.firstName || ''} ${item.otherNames || ''} - ${item.customerID || ''}`,
            }
          }, {})
        }
      } catch (error) {
        this.alertError({ text: this.$t('Failed to fetch account referees') })
      }
    },
    onViewPassport(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s ${this.$t('Passport')}`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    onViewSignature(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s ${this.$t('Signature')}`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    async onSelectCustomerAccountItem(items) {
      try {
        this.showSpinner = true

        const searchData = {
          search: {
            AccountNumber: items.corporateAccountNumber,
          },
          page: 1,
          limit: 10,
          permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
        }
        const response = await this.searchCustomerAccount(searchData)

        if (!response || !response.data || !response.data.data || !Array.isArray(response.data.data) || response.data.data.length === 0) {
          this.alertError({ text: this.$t('No customer account information found') })
          return
        }

        const { status, data } = response

        const corporateCustomerInfo = data.data[0]

        this.documentIdentifier = corporateCustomerInfo.documentIdentifier || ''
        this.documentsFormData = corporateCustomerInfo.documents
          ? corporateCustomerInfo.documents.map(item => ({
            url: item.url || '',
            type: item.typeID || '',
            id: item.id || '',
            fileName: item.type || '',
            fileExtension: item.fileExtension || '',
          }))
          : [
            {
              url: '',
              type: '',
            },
          ]
        this.groupMemberList = []
        if (corporateCustomerInfo.mandateRuleIndex !== null) {
          this.mandateList = transformMandateRule(corporateCustomerInfo.mandateRuleIndex)
        }

        if (corporateCustomerInfo.accountNumber) {
          this.fetchAccountReferees(corporateCustomerInfo.accountNumber)
        }

        if (corporateCustomerInfo.introducerType === 'BankStaff' && corporateCustomerInfo.introducerID) {
          this.fetchAccountOfficerByStaffID(corporateCustomerInfo.introducerID)
        }
        if (corporateCustomerInfo.introducerType === 'Customer' && corporateCustomerInfo.introducerID) {
          this.fetchAccountByCustomerID(corporateCustomerInfo.introducerID)
        }

        if (corporateCustomerInfo.isGroupAccount && corporateCustomerInfo.customerID) {
          try {
            const customerSignatory = await this.getAccountSignatories({
              accountNumber: corporateCustomerInfo.accountNumber,
              permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
            })

            const enrolledUsersResponse = await this.getCustomerProfile({
              search: { customerID: corporateCustomerInfo.customerID },
              page: this.currentPage,
              limit: this.perPage,
              permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
            })

            const { status: enrolledStatus, data: enrolledData } = enrolledUsersResponse
            if (enrolledStatus === 200 && enrolledData?.data && enrolledData?.data.length > 0) {
              // Get enrolled users from getCustomerProfile response
              const enrolledUsers = enrolledData.data || []

              // Get signatories from getAccountSignatories response
              const accountSignatories = customerSignatory?.data?.data || []

              // Create a map of enrolled users by email for quick lookup
              const enrolledUsersMap = new Map(
                enrolledUsers.map(user => [user.email?.toLowerCase(), user]),
              )

              // Merge the two arrays and add enrollment status
              this.signatoryList = accountSignatories.map(signatory => {
                const signatoryEmail = signatory.email?.toLowerCase()
                const enrolledUser = enrolledUsersMap.get(signatoryEmail)

                return {
                  fullName: signatory.name || `${signatory.firstName || ''} ${signatory.lastName || ''}`.trim(),
                  passport: signatory.passportFileUrl || signatory.imageUrl || '',
                  signature: signatory.signatureFileUrl || signatory.imageUrl || '',
                  email: signatory.email || '',
                  customerID: signatory.customerID || '',
                  // If user is found in enrolled users, they are enrolled
                  isEnrolled: !!enrolledUser,
                  corporateAccountNumber: items.corporateAccountNumber,
                  id: enrolledUser?.id || '',
                }
              })
            }
          } catch (signatoryError) {
            console.error('Error fetching signatories:', signatoryError)
            this.signatoryList = []
          }
        }

        if (!corporateCustomerInfo.isGroupAccount) {
          try {
            const customerSignatory = await this.getCustomerInformation({
              customerID: corporateCustomerInfo.customerID,
              permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
            })
            this.signatoryList = customerSignatory && customerSignatory.data && customerSignatory.data.data
              ? [
                {
                  fullName: customerSignatory.data.data.name || '',
                  passport: customerSignatory.data.data.passportFileUrl || '',
                  signature: customerSignatory.data.data.signatureFileUrl || '',
                },
              ]
              : []
          } catch (customerError) {
            console.error('Error fetching customer information:', customerError)
            this.signatoryList = []
          }
        }

        if (corporateCustomerInfo.isGroupAccount) {
          try {
            const groupResponse = await this.getGroupMembers({
              formData: {
                groupCustomerID: corporateCustomerInfo.customerID,
                pageNumber: 0,
                pageSize: 0,
              },
              permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
            })

            this.groupMemberList = groupResponse && groupResponse.data && groupResponse.data.data
              ? groupResponse.data.data
              : []
            this.recordCount = groupResponse && groupResponse.data ? groupResponse.data.recordCount || 0 : 0
          } catch (groupError) {
            console.error('Error fetching group members:', groupError)
            this.groupMemberList = []
            this.recordCount = 0
          }
        }

        if (status === 200 && data && data.data) {
          this.timeout = setTimeout(() => {
            let productInterests
            let productFees

            // Safely access data properties
            this.customer.applyCreditInterest = data.data.applyCreditInterest || ''
            this.customer.applyFee = data.data.applyFee || ''

            if (data.data.applyCreditInterest === 'True') {
              this.customer.interestRate = data.data.interestRate || null
            }
            if (data.data.applyFee === 'True') {
              this.customer.flatAmount = data.data.flatAmount === 0 ? '0' : formatAmount(data.data.flatAmount || 0)
              this.customer.flatAmountPerTransaction = data.data.flatAmountPerTransaction === 0 ? '0' : formatAmount(data.data.flatAmountPerTransaction || 0)
              this.customer.percentageOnTurnover = data.data.percentageOnTurnover === 0 ? '0' : (data.data.percentageOnTurnover || 0)
              this.customer.maximumAmount = data.data.maximumAmount === 0 ? '0' : formatAmount(data.data.maximumAmount || 0)
              this.customer.minimumAmount = data.data.minimumAmount === 0 ? '0' : formatAmount(data.data.minimumAmount || 0)
            }

            if (data.data.applyCreditInterest !== null) {
              this.setApplyCreditInterest(data.data.applyCreditInterest)

              if (data.data?.productInterests && Array.isArray(data.data.productInterests) && data.data.productInterests.length > 0) {
                this.interestConditionBasis = data.data.productInterests[0].interestConditionBasis || ''
                this.isInterestNoOfWithdrawal = data.data.productInterests[0].interestConditionBasis === 'NoOfWithdrawals'

                productInterests = data.data.productInterests.map(item => ({
                  ...item,
                  interestConditionValue: !this.isInterestNoOfWithdrawal
                    ? formatAmount(item.interestConditionValue || 0)
                    : (item.interestConditionValue || 0),
                  interestRate: convertZeroToString(item.interestRate || 0),
                }))

                this.customer.productInterests = productInterests
              }
            }

            if (data.data.applyFee !== null) {
              this.setApplyFee(data.data.applyFee)
              if (data.data?.productFees && Array.isArray(data.data.productFees) && data.data.productFees.length > 0) {
                this.feeConditionBasis = data.data.productFees[0].feeConditionBasis || ''
                this.isFeeNoOfWithdrawal = data.data.productFees[0].feeConditionBasis === 'NoOfWithdrawals'
                productFees = data.data.productFees.map(item => ({
                  ...item,
                  flatAmount: item.flatAmount === 0 ? '0' : formatAmount(item.flatAmount || 0),
                  flatAmountPerTransaction:
                    item.flatAmountPerTransaction === 0 ? '0' : formatAmount(item.flatAmountPerTransaction || 0),
                  percentageOnTurnover: convertZeroToString(item.percentageOnTurnover || 0),
                  minimumAmount: item.minimumAmount === 0 ? '0' : formatAmount(item.minimumAmount || 0),
                  maximumAmount: item.maximumAmount === 0 ? '0' : formatAmount(item.maximumAmount || 0),
                  feeConditionValue: !this.isFeeNoOfWithdrawal
                    ? formatAmount(item.flatAmount || 0)
                    : convertZeroToString(item.feeConditionValue || 0),
                }))

                this.customer.productFees = productFees
              }
            }
          }, 1000)

          this.customer = {
            ...corporateCustomerInfo,
            minimumBalanceRequired: corporateCustomerInfo.minimumBalanceRequired ? toBalance(corporateCustomerInfo.minimumBalanceRequired) : null,
            overrideProductApplicableInterest: data.data.overrideProductApplicableInterest || false,
          }
        }
        this.modalTitle = this.$t('Customer Account')
        this.showSpinner = false
        this.$root.$emit('bv::show::modal', 'modal-lg')

        if (data && data.data && data.data.memos !== null) {
          this.memoGetData = data.data.memos || []
          this.$root.$emit('bv::show::modal', 'memo-info-modal')
        }
      } catch (error) {
        console.error('Error in onSelectCustomerAccountItem:', error)
        this.alertError({ text: this.$t('Failed to load customer account details') })
      } finally {
        this.showSpinner = false
      }
    },
    groupMemberTableIndex(index) {
      return this.tableCurrentPage * this.tablePerPage - this.tablePerPage + index + 1
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCustomerProfile()
    },
    resetModal() {
      this.customerProfileFormData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.buttonLabel = this.$t('Save')
    },
    resetAvatarModal() {
      this.avatarLg = ''
      this.avatarModalTitle = ''
    },
    async fetchAccountOfficerByStaffID(staffID) {
      try {
        const response = await this.getAccountOfficerByStaffID({
          staffID,
          permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
        })
        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.accountIntroducerList = {
            ...data.data,
            customerName: data.data.name,
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchAccountByCustomerID(customerID) {
      try {
        const response = await this.getAccountsByCustomerID({
          customerID,
          permission: eChannelsPermission.VIEW_CORPORATE_INTERNET_USERS,
        })
        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.accountIntroducerList = {
            ...data.data,
            customerName: `${data.data?.lastName ?? ''} ${data.data?.firstName ?? ''} ${data.data?.otherNames ?? ''}`,
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    setApplyCreditInterest(selectedValue) {
      if (selectedValue === 'True') {
        this.isCreditInterest = true
        this.isConditional = false
        this.showAddProductInterest = false
        this.customer.productInterests = []
      } else if (selectedValue === 'False') {
        this.isCreditInterest = false
        this.isConditional = false
        this.showAddProductInterest = false
        this.customer.interestRate = 0
        this.customer.productInterests = []
      } else {
        this.isCreditInterest = false
        this.isConditional = true
        this.customer.interestRate = 0
      }
    },
    setApplyFee(selectedValue) {
      if (selectedValue === 'True') {
        this.isApplyFee = true
        this.isConditionalFee = false
        this.customer.productFees = []
      } else if (selectedValue === 'False') {
        this.isApplyFee = false
        this.isConditionalFee = false
        this.customer.productFees = []
        this.customer.flatAmount = null
        this.customer.flatAmountPerTransaction = null
        this.customer.percentageOnTurnover = null
        this.customer.maximumAmount = null
        this.customer.minimumAmount = null
      } else {
        this.isApplyFee = false
        this.isConditionalFee = true
        this.customer.flatAmount = null
        this.customer.flatAmountPerTransaction = null
        this.customer.percentageOnTurnover = null
        this.customer.maximumAmount = null
        this.customer.minimumAmount = null
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.upload-image{
    border: 2px dashed #DCD9D9;
    position: relative;
    width: 250px;
    height: 250px
}
.format {
    color: #999999 !important;
}
form input[type=file]{
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}
.upload-instruction{
  padding-top: 22%;
  color: #000;
  text-align: center;
}
.trash {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-right: 10px;
  cursor: pointer;
}
.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/base/bootstrap-extended/_include.scss';
</style>

<style lang="scss" scoped>
@import '../../../../@core/styles/global.css';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
